/**
 * Spec 014b US-3: the "Car settings" admin card. Each car setting is set by
 * the hosts, or by the brand with a value; taking a setting without its value
 * is not possible (the form refuses, and so does the server). Plain functions,
 * tested in `carSettingsAdmin.test.ts`.
 */
import type { CarSettingKey } from '../../utils/hostCapabilities';
import type { DistanceUnit } from '../../utils/units';
import type { CurrencyCode } from './regionAdmin';

export type CarSettingRuleKey =
  | 'hourlyPricing'
  | 'monthlyPricing'
  | 'discounts'
  | 'protectionDecline'
  | 'ageLimit'
  | 'tripPreferences'
  | 'mileage'
  | 'deposit'
  | 'guestInstructions'
  | 'delivery';

export interface FieldDef {
  key: string;
  label: string;
  kind: 'number' | 'integer' | 'boolean' | 'text';
  min?: number;
  max?: number;
  /** Blank is allowed and saved as null; `blankMeans` says what null does. */
  nullable?: boolean;
  blankMeans?: string;
  suffix?: string;
  /** Prefilled when the admin first picks the mode. */
  defaultValue?: string;
}

export interface BrandModeDef {
  mode: string;
  label: string;
  fields: FieldDef[];
}

export interface CarSettingDef {
  rule: CarSettingRuleKey;
  capability: CarSettingKey;
  title: string;
  /** The mode that means "set by host". */
  hostMode: string;
  brandModes: BrandModeDef[];
  /** What hosts stop seeing on the car page. */
  hides: string;
}

const percent = (key: string, label: string): FieldDef => ({
  key,
  label,
  kind: 'number',
  min: 0,
  max: 90,
  suffix: '%',
  defaultValue: '0',
});

export const CAR_SETTINGS: readonly CarSettingDef[] = [
  {
    rule: 'hourlyPricing',
    capability: 'carHourlyPricing',
    title: 'Hourly pricing',
    hostMode: 'perCar',
    hides: 'the hourly pricing section',
    brandModes: [
      { mode: 'off', label: 'Not offered', fields: [] },
      {
        mode: 'fromDaily',
        label: 'From the daily rate',
        fields: [
          {
            key: 'divisor',
            label: 'Hourly price = daily rate ÷',
            kind: 'number',
            min: 1,
            max: 24,
            defaultValue: '2.5',
          },
        ],
      },
    ],
  },
  {
    rule: 'monthlyPricing',
    capability: 'carMonthlyPricing',
    title: 'Monthly pricing',
    hostMode: 'perCar',
    hides: 'the monthly pricing section and monthly miles',
    brandModes: [
      { mode: 'off', label: 'Not offered', fields: [] },
      {
        mode: 'fromDaily',
        label: 'From the daily rate',
        fields: [
          percent('discountPercent30', '30 days off'),
          percent('discountPercent60', '60 days off'),
          percent('discountPercent90', '90 days off'),
          {
            key: 'monthlyMiles',
            label: 'Miles per month',
            kind: 'integer',
            min: 1,
            max: 100000,
            nullable: true,
            blankMeans: 'unlimited',
          },
        ],
      },
    ],
  },
  {
    rule: 'discounts',
    capability: 'carDiscounts',
    title: 'Discounts',
    hostMode: 'perCar',
    hides: 'the discounts section (the last-minute surcharge stays with hosts)',
    brandModes: [
      {
        mode: 'brand',
        label: 'One discount table',
        fields: [
          percent('threeDays', '3+ days'),
          percent('oneWeek', '1+ week'),
          percent('twoWeeks', '2+ weeks'),
          percent('threeWeeks', '3+ weeks'),
          percent('oneMonth', '1+ month'),
          percent('twoMonths', '2+ months'),
          percent('threeMonths', '3+ months'),
        ],
      },
    ],
  },
  {
    rule: 'protectionDecline',
    capability: 'carProtectionDecline',
    title: 'Declining protection',
    hostMode: 'perCar',
    hides: 'the "guests may decline protection" switch',
    brandModes: [
      {
        mode: 'brand',
        label: 'One rule for every car',
        fields: [{ key: 'allowed', label: 'Guests may decline protection', kind: 'boolean' }],
      },
    ],
  },
  {
    rule: 'ageLimit',
    capability: 'carAgeLimit',
    title: 'Age limit',
    hostMode: 'perCar',
    hides: 'the age limit section',
    brandModes: [
      {
        mode: 'brand',
        label: 'One age for every car',
        fields: [{ key: 'age', label: 'Minimum driver age', kind: 'integer', min: 16, max: 99 }],
      },
    ],
  },
  {
    rule: 'tripPreferences',
    capability: 'carTripPreferences',
    title: 'Trip preferences',
    hostMode: 'perCar',
    hides: 'the trip preferences section',
    brandModes: [
      {
        mode: 'brand',
        label: 'One set for every car',
        fields: [
          {
            key: 'advanceNoticeHours',
            label: 'Advance notice',
            kind: 'number',
            min: 0,
            max: 720,
            suffix: 'hours',
            defaultValue: '3',
          },
          {
            key: 'tripBufferHours',
            label: 'Time between trips',
            kind: 'number',
            min: 0,
            max: 72,
            suffix: 'hours',
            defaultValue: '3',
          },
          {
            key: 'minimumRentalDays',
            label: 'Shortest trip',
            kind: 'integer',
            min: 1,
            max: 365,
            nullable: true,
            blankMeans: 'no minimum',
            suffix: 'days',
          },
          {
            key: 'maximumRentalDays',
            label: 'Longest trip',
            kind: 'integer',
            min: 1,
            max: 365,
            nullable: true,
            blankMeans: 'no maximum',
            suffix: 'days',
          },
        ],
      },
    ],
  },
  {
    rule: 'mileage',
    capability: 'carDistanceLimits',
    title: 'Distance limits',
    hostMode: 'perCarDaily',
    hides: 'the daily distance limits (monthly miles follow monthly pricing)',
    brandModes: [
      {
        mode: 'perPeriod',
        label: 'A mileage allowance',
        fields: [
          { key: 'miles', label: 'Miles', kind: 'integer', min: 1, max: 100000 },
          {
            key: 'days',
            label: 'Per',
            kind: 'integer',
            min: 1,
            max: 365,
            suffix: 'days',
            defaultValue: '1',
          },
          {
            key: 'overagePerMile',
            label: 'Price per extra mile',
            kind: 'number',
            min: 0,
            max: 100,
            suffix: '$',
          },
        ],
      },
      { mode: 'unlimited', label: 'Unlimited distance', fields: [] },
    ],
  },
  {
    rule: 'deposit',
    capability: 'carDeposit',
    title: 'Security deposit',
    hostMode: 'perCar',
    hides: 'the security deposit section',
    brandModes: [
      {
        mode: 'weeksOfRent',
        label: 'Weeks of rent',
        fields: [
          {
            key: 'weeks',
            label: 'Weeks (× 7 × daily rate)',
            kind: 'integer',
            min: 1,
            max: 8,
            defaultValue: '1',
          },
        ],
      },
      {
        mode: 'flat',
        label: 'A fixed amount',
        fields: [
          { key: 'amount', label: 'Amount', kind: 'number', min: 0, max: 100000, suffix: '$' },
        ],
      },
    ],
  },
  {
    rule: 'guestInstructions',
    capability: 'carGuestInstructions',
    title: 'Guest instructions',
    hostMode: 'perCar',
    hides: 'the guest instructions section',
    brandModes: [
      {
        mode: 'brand',
        label: 'One text for every trip',
        fields: [{ key: 'text', label: 'Instructions', kind: 'text', max: 500 }],
      },
    ],
  },
  {
    // Spec 014c: no brand value. "Not offered" keeps every stored airport for later.
    rule: 'delivery',
    capability: 'carDelivery',
    title: 'Delivery locations',
    hostMode: 'perCar',
    hides: 'the car page Delivery locations section; guests on this site see no airport delivery',
    brandModes: [{ mode: 'off', label: 'Not offered', fields: [] }],
  },
];

/** One row of the form: the chosen mode and every field typed so far (kept when the mode changes). */
export interface SettingForm {
  mode: string;
  values: Record<string, string>;
}
export type CarSettingsForm = Record<CarSettingRuleKey, SettingForm>;
export type CarSettingsErrors = Partial<Record<CarSettingRuleKey, Record<string, string>>>;

type Rules = Record<string, unknown> | null | undefined;

function stored(rules: Rules, def: CarSettingDef): Record<string, unknown> {
  const value = rules?.[def.rule];
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : { mode: def.hostMode };
}

export function toCarSettingsForm(rules: Rules): CarSettingsForm {
  const form = {} as CarSettingsForm;
  for (const def of CAR_SETTINGS) {
    const value = stored(rules, def);
    const values: Record<string, string> = {};
    for (const mode of def.brandModes) {
      for (const field of mode.fields) {
        if (values[field.key] !== undefined) continue;
        const current = mode.mode === value.mode ? value[field.key] : undefined;
        values[field.key] =
          current === null || current === undefined
            ? field.kind === 'boolean'
              ? 'false'
              : (field.defaultValue ?? '')
            : String(current);
      }
    }
    form[def.rule] = { mode: String(value.mode ?? def.hostMode), values };
  }
  return form;
}

/** The rule value a row asks for, or the errors keyed by field. */
export function settingValue(
  def: CarSettingDef,
  row: SettingForm,
): { value?: Record<string, unknown>; errors?: Record<string, string> } {
  if (row.mode === def.hostMode) {
    return { value: { mode: def.hostMode } };
  }
  const mode = def.brandModes.find(item => item.mode === row.mode);
  if (!mode) {
    return { errors: { mode: 'Choose an option' } };
  }
  const value: Record<string, unknown> = { mode: mode.mode };
  const errors: Record<string, string> = {};
  for (const field of mode.fields) {
    const raw = (row.values[field.key] ?? '').trim();
    if (field.kind === 'boolean') {
      value[field.key] = raw === 'true';
      continue;
    }
    if (field.kind === 'text') {
      if (!raw) errors[field.key] = 'Required';
      else if (field.max !== undefined && raw.length > field.max)
        errors[field.key] = `At most ${field.max} characters`;
      value[field.key] = raw;
      continue;
    }
    if (!raw) {
      if (field.nullable) value[field.key] = null;
      else errors[field.key] = 'Required';
      continue;
    }
    const number = Number(raw);
    if (!Number.isFinite(number)) errors[field.key] = 'Enter a number';
    else if (field.kind === 'integer' && !Number.isInteger(number))
      errors[field.key] = 'Enter a whole number';
    else if (field.min !== undefined && number < field.min)
      errors[field.key] = `At least ${field.min}`;
    else if (field.max !== undefined && number > field.max)
      errors[field.key] = `At most ${field.max}`;
    value[field.key] = number;
  }
  if (
    def.rule === 'tripPreferences' &&
    typeof value.minimumRentalDays === 'number' &&
    typeof value.maximumRentalDays === 'number' &&
    value.minimumRentalDays > value.maximumRentalDays
  ) {
    errors.minimumRentalDays = 'Must not be longer than the longest trip';
  }
  return Object.keys(errors).length > 0 ? { errors } : { value };
}

/**
 * The `bookingRules` patch: only the rows that changed. Any invalid row
 * blocks the save (FR-012: a brand value needs every field).
 */
export function carSettingsPatch(
  rules: Rules,
  form: CarSettingsForm,
): { patch: Record<string, unknown>; errors: CarSettingsErrors } {
  const patch: Record<string, unknown> = {};
  const errors: CarSettingsErrors = {};
  for (const def of CAR_SETTINGS) {
    const result = settingValue(def, form[def.rule]);
    if (result.errors) {
      errors[def.rule] = result.errors;
      continue;
    }
    if (JSON.stringify(sorted(result.value)) !== JSON.stringify(sorted(stored(rules, def)))) {
      patch[def.rule] = result.value;
    }
  }
  return { patch, errors };
}

function sorted(value: unknown): unknown {
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)),
  );
}

/** Server errors (`details.errors[].path`, e.g. `bookingRules.mileage.miles`) by row and field. */
export function errorsFromServer(
  errors: Array<{ path?: string; message?: string }> | undefined,
): CarSettingsErrors {
  const result: CarSettingsErrors = {};
  for (const error of errors ?? []) {
    const [, rule, field] = (error.path ?? '').split('.');
    const def = CAR_SETTINGS.find(item => item.rule === rule);
    if (!def) continue;
    result[def.rule] = {
      ...(result[def.rule] ?? {}),
      [field ?? 'mode']: error.message ?? 'Invalid',
    };
  }
  return result;
}

/** "$0.5" reads "$0.50"; whole amounts drop ".00" (today's wording). Same "$" for CAD (spec D14). */
const moneyIn = (value: unknown, _currency: CurrencyCode = 'USD') =>
  `$${Number(value).toFixed(2).replace(/\.00$/, '')}`;

/**
 * The brand value in words, for the admin list and the host car page
 * ("200 miles per day, $0.50 per extra mile"). Null when hosts set it.
 */
export function describeCarSetting(
  rule: CarSettingRuleKey,
  value: unknown,
  /** Spec 028: the brand's unit; brand distance rules are stored in it. */
  unit: DistanceUnit = 'mi',
  currency: CurrencyCode = 'USD',
): string | null {
  const v = (value ?? {}) as Record<string, unknown>;
  const unitWord = unit === 'km' ? 'km' : 'miles';
  const unitSingular = unit === 'km' ? 'km' : 'mile';
  const money = (amount: unknown) => moneyIn(amount, currency);
  const def = CAR_SETTINGS.find(item => item.rule === rule);
  if (!def || !v.mode || v.mode === def.hostMode) return null;
  switch (`${rule}:${v.mode}`) {
    case 'hourlyPricing:off':
    case 'monthlyPricing:off':
    case 'delivery:off':
      return 'Not offered';
    case 'hourlyPricing:fromDaily':
      return `Daily rate ÷ ${v.divisor} per hour`;
    case 'monthlyPricing:fromDaily':
      return `Daily rate × days, ${v.discountPercent30}% / ${v.discountPercent60}% / ${v.discountPercent90}% off for 30 / 60 / 90 days, ${v.monthlyMiles === null ? 'unlimited distance' : `${v.monthlyMiles} ${unitWord} a month`}`;
    case 'discounts:brand':
      return `${v.threeDays}% for 3+ days, ${v.oneWeek}% for a week, ${v.oneMonth}% for a month`;
    case 'protectionDecline:brand':
      return v.allowed ? 'Guests may decline protection' : 'Guests may not decline protection';
    case 'ageLimit:brand':
      return `Drivers ${v.age} or older`;
    case 'tripPreferences:brand':
      return `${v.advanceNoticeHours} hours notice, ${v.tripBufferHours} hours between trips${v.minimumRentalDays ? `, at least ${v.minimumRentalDays} days` : ''}${v.maximumRentalDays ? `, at most ${v.maximumRentalDays} days` : ''}`;
    case 'mileage:perPeriod':
      return `${v.miles} ${unitWord} per ${v.days === 1 ? 'day' : `${v.days} days`}, ${money(v.overagePerMile)} per extra ${unitSingular}`;
    case 'mileage:unlimited':
      return 'Unlimited distance';
    case 'deposit:weeksOfRent':
      return `${v.weeks} week${v.weeks === 1 ? '' : 's'} of rent`;
    case 'deposit:flat':
      return money(v.amount);
    case 'guestInstructions:brand':
      return String(v.text ?? '');
    default:
      return null;
  }
}

export const CAR_SETTING_TITLES: Readonly<Record<string, string>> = Object.fromEntries(
  CAR_SETTINGS.map(def => [def.rule, def.title]),
);

/** Spec 014c: whether a stored `delivery` rule offers delivery (missing = set by host). */
export function deliveryOffered(rules: Rules): boolean {
  const value = rules?.delivery as { mode?: unknown } | undefined;
  return value?.mode !== 'off';
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export interface DeliveryConfirmation {
  title: string;
  message: string;
  confirmText: string;
}

/**
 * Spec 014c FR-011, FR-017: the question asked before a save that turns
 * delivery off or back on; null when the save leaves delivery as it is.
 * `hosts` is `deliveryHostCount` (who gets told), `trips` is
 * `openDeliveryTripCount` (booked trips that keep their delivery).
 */
export function deliveryConfirmation(
  rules: Rules,
  patch: Record<string, unknown>,
  { hosts, trips }: { hosts: number; trips: number },
): DeliveryConfirmation | null {
  if (!('delivery' in patch)) return null;
  const before = deliveryOffered(rules);
  const after = deliveryOffered(patch);
  if (before === after) return null;
  const notified =
    hosts === 0
      ? 'No hosts have airport delivery set up.'
      : `${count(hosts, 'host', 'hosts')} will be notified.`;
  if (!after) {
    return {
      title: 'Stop offering delivery?',
      message: `${notified} ${count(trips, 'booked trip', 'booked trips')} with delivery ${trips === 1 ? 'is' : 'are'} not changed.`,
      confirmText: 'Stop delivery',
    };
  }
  return {
    title: 'Offer delivery again?',
    message: `Make sure your insurance covers the delivery period before you offer delivery again. ${notified}`,
    confirmText: 'Offer delivery',
  };
}

/**
 * Spec 028: the car settings with labels, suffixes and limits in the brand's
 * unit (spec D6). The keys stay the same: `miles` and `overagePerMile` hold
 * numbers in the brand's unit.
 */
export function carSettingsFor(unit: DistanceUnit): readonly CarSettingDef[] {
  if (unit === 'mi') return CAR_SETTINGS;
  return CAR_SETTINGS.map(def => ({
    ...def,
    hides: def.hides.replace('monthly miles', 'monthly distance'),
    brandModes: def.brandModes.map(mode => ({
      ...mode,
      label: mode.label === 'A mileage allowance' ? 'A distance allowance' : mode.label,
      fields: mode.fields.map(field => {
        if (def.rule === 'mileage' && field.key === 'miles') {
          return { ...field, label: 'Kilometres', max: 160000 };
        }
        if (def.rule === 'mileage' && field.key === 'overagePerMile') {
          return { ...field, label: 'Price per extra km', max: 60 };
        }
        if (def.rule === 'monthlyPricing' && field.key === 'monthlyMiles') {
          return { ...field, label: 'Kilometres per month', max: 160000 };
        }
        return field;
      }),
    })),
  }));
}
